import MaskField from '@components/v2/form/MaskField/MaskField';
import PhoneField from '@components/v2/form/PhoneField/PhoneField';
import Select from '@components/v2/form/Select/Select';
import TextField from '@components/v2/form/TextField/TextField';
import { Box, Grid } from '@mui/material';
import { forwardRef } from 'react';
import { Controller, FormProvider } from 'react-hook-form';
import USAddress from '../../components/Address/USAddress';
import Typography from '../../components/v2/ui/Typography/Typography';
import LangEnum from '../../lang/LangEnum';
import { cnpjRegex } from '../../utils/regexes';
import { StoreSignUpTestIds } from '../../utils/testIds/storeSignUp.testIds';
import { internationalDocumentTypes } from '../customer/utils/customerDocumentTypes.utils';
import { useTranslation } from '../translation/TranslationContext';
import AddressSection from './AddressSection';
import useRegisterStepSubmit from './hooks/useRegisterStepSubmit';
import useStoreStepForm from './hooks/useStoreStepForm';

const hiddenAddressFields = ['code'];

const StoreStep = forwardRef<HTMLDivElement>((_, ref) => {
  const { translate, translateOptions } = useTranslation();
  const { form, isInternational, handleSave, handleChangeCountryCode, isSubmitting } =
    useStoreStepForm();

  const { control } = form;

  useRegisterStepSubmit({ submit: handleSave, isSubmitting });

  return (
    <Box
      ref={ref}
      sx={{
        maxWidth: 800,
      }}
    >
      <FormProvider {...form}>
        <form onSubmit={handleSave}>
          <Grid container columnSpacing={1} rowSpacing={1.5}>
            <Grid size={12}>
              <Typography testId={StoreSignUpTestIds.STORE_STEP_INTRO}>
                {translate(LangEnum.SIGN_UP_STORE_STEP_INTRO)}
              </Typography>
            </Grid>
            <Grid size={12} sx={{ mt: 1.5 }}>
              <Typography variant="overline" color="text.primary">
                {translate(LangEnum.GLOBAL_GENERAL_DATA)}
              </Typography>
            </Grid>
            {isInternational ? (
              <>
                <Grid
                  size={{
                    xs: 12,
                    sm: 6,
                  }}
                >
                  <Controller
                    name="documentType"
                    control={control}
                    render={({ field, fieldState }) => (
                      <Select
                        {...field}
                        value={field.value ?? ''}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        label={translate(LangEnum.CUSTOMERS_FORM_DOC_TYPE_LABEL)}
                        options={translateOptions(internationalDocumentTypes)}
                      />
                    )}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    sm: 6,
                  }}
                >
                  <Controller
                    name="cpfcnpj"
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        value={field.value ?? ''}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        label={translate(LangEnum.CUSTOMERS_FORM_DOC_NUMBER_LABEL)}
                        slotProps={{ htmlInput: { maxLength: 50 } }}
                      />
                    )}
                  />
                </Grid>
              </>
            ) : (
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                }}
              >
                <Controller
                  name="cpfcnpj"
                  control={control}
                  render={({ field, fieldState }) => (
                    <MaskField
                      {...field}
                      value={field.value ?? ''}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      disabled
                      mask={cnpjRegex}
                      label={translate(LangEnum.STORE_STEP_FORM_CNPJ_LABEL)}
                      slotProps={{ htmlInput: { 'test-id': StoreSignUpTestIds.STORE_CNPJ } }}
                    />
                  )}
                />
              </Grid>
            )}
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <Controller
                name="companyName"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    label={translate(LangEnum.GLOBAL_COMPANY_NAME)}
                    slotProps={{ htmlInput: { 'test-id': StoreSignUpTestIds.STORE_COMPANY_NAME } }}
                  />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <Controller
                name="commercialName"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    label={translate(LangEnum.STORE_STEP_FORM_COMMERCIAL_NAME)}
                    slotProps={{
                      htmlInput: { 'test-id': StoreSignUpTestIds.STORE_COMMERCIAL_NAME },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <Controller
                name="stateInscription"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    label={translate(LangEnum.GLOBAL_STATE_INSCRIPTION)}
                  />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <Controller
                name="email"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    label={translate(LangEnum.STORE_STEP_FORM_EMAIL)}
                    slotProps={{ htmlInput: { 'test-id': StoreSignUpTestIds.STORE_STORE_EMAIL } }}
                  />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <Controller
                name="phone"
                control={control}
                render={({ field, fieldState }) => (
                  <PhoneField
                    name={field.name}
                    value={field.value ?? ''}
                    onBlur={field.onBlur}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    label={translate(LangEnum.STORE_STEP_FORM_PHONE)}
                    onChange={(value, data) => {
                      field.onChange(value);
                      handleChangeCountryCode(data.dialCode);
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={12} sx={{ mt: 2.5 }}>
              <Typography variant="overline" color="text.primary">
                {translate(LangEnum.GLOBAL_ADDRESS_LABEL)}
              </Typography>
            </Grid>
            <Grid size={12}>
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                {isInternational ? (
                  <USAddress hiddenFields={hiddenAddressFields} />
                ) : (
                  <AddressSection />
                )}
              </Box>
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </Box>
  );
});

export default StoreStep;
